/**
 * Correlation and Clustering Utilities for IQUP Analysis
 */

/**
 * Calculate Pearson correlation coefficient between two arrays
 */
export function pearsonCorrelation(x, y) {
  const n = x.length
  if (n !== y.length || n === 0) return 0

  const sumX = x.reduce((a, b) => a + b, 0)
  const sumY = y.reduce((a, b) => a + b, 0)
  const sumXY = x.reduce((acc, xi, i) => acc + xi * y[i], 0)
  const sumX2 = x.reduce((acc, xi) => acc + xi * xi, 0)
  const sumY2 = y.reduce((acc, yi) => acc + yi * yi, 0)

  const numerator = n * sumXY - sumX * sumY
  const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY))

  if (denominator === 0) return 0
  return numerator / denominator
}

/**
 * Calculate correlation matrix for a set of dimensions
 * @param {Array} data - Array of objects with dimension values
 * @param {Array} dimensions - Array of dimension keys to correlate
 * @returns {Object} - { matrix: 2D array, labels: dimension names }
 */
export function calculateCorrelationMatrix(data, dimensions) {
  const n = dimensions.length
  const matrix = Array(n).fill(null).map(() => Array(n).fill(0))

  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      if (i === j) {
        matrix[i][j] = 1
      } else if (j > i) {
        const x = data.map(d => d[dimensions[i]])
        const y = data.map(d => d[dimensions[j]])
        const r = pearsonCorrelation(x, y)
        matrix[i][j] = r
        matrix[j][i] = r // Symmetric
      }
    }
  }

  return { matrix, labels: dimensions }
}

/**
 * Hierarchical clustering using average linkage
 * Returns an ordering of indices that groups similar items together
 */
export function hierarchicalClusteringOrder(distanceMatrix) {
  const n = distanceMatrix.length
  if (n <= 1) return [0]
  if (n === 2) return [0, 1]

  // Initialize clusters: each item starts as its own cluster
  let clusters = Array(n).fill(null).map((_, i) => ({
    id: i,
    elements: [i],
    left: null,
    right: null
  }))

  // Distance between clusters (start with original distances)
  const clusterDist = distanceMatrix.map(row => [...row])

  // Track which clusters are still active
  const active = new Set(Array(n).fill(null).map((_, i) => i))

  let nextClusterId = n

  // Merge until only one cluster remains
  while (active.size > 1) {
    // Find minimum distance pair
    let minDist = Infinity
    let minI = -1
    let minJ = -1

    const activeArr = [...active]
    for (let i = 0; i < activeArr.length; i++) {
      for (let j = i + 1; j < activeArr.length; j++) {
        const ci = activeArr[i]
        const cj = activeArr[j]
        if (clusterDist[ci][cj] < minDist) {
          minDist = clusterDist[ci][cj]
          minI = ci
          minJ = cj
        }
      }
    }

    // Merge clusters minI and minJ
    const merged = {
      id: nextClusterId,
      elements: [...clusters[minI].elements, ...clusters[minJ].elements],
      left: clusters[minI],
      right: clusters[minJ]
    }

    // Update distance matrix (average linkage)
    clusterDist.push([])
    for (let k = 0; k < nextClusterId; k++) {
      if (active.has(k)) {
        const ni = clusters[minI].elements.length
        const nj = clusters[minJ].elements.length
        const newDist = (clusterDist[minI][k] * ni + clusterDist[minJ][k] * nj) / (ni + nj)
        clusterDist[nextClusterId][k] = newDist
        clusterDist[k][nextClusterId] = newDist
      } else {
        clusterDist[nextClusterId][k] = Infinity
        if (!clusterDist[k]) clusterDist[k] = []
        clusterDist[k][nextClusterId] = Infinity
      }
    }
    clusterDist[nextClusterId][nextClusterId] = 0

    clusters.push(merged)
    active.delete(minI)
    active.delete(minJ)
    active.add(nextClusterId)
    nextClusterId++
  }

  // Extract leaf order from final tree (in-order traversal)
  function getLeafOrder(node) {
    if (!node.left && !node.right) {
      return node.elements
    }
    return [...getLeafOrder(node.left), ...getLeafOrder(node.right)]
  }

  const finalCluster = clusters[clusters.length - 1]
  return getLeafOrder(finalCluster)
}

/**
 * Convert correlation matrix to distance matrix for clustering
 * Distance = 1 - |correlation|
 */
export function correlationToDistance(correlationMatrix) {
  return correlationMatrix.map(row =>
    row.map(r => 1 - Math.abs(r))
  )
}

/**
 * Reorder a matrix according to a given index ordering
 */
export function reorderMatrix(matrix, order) {
  const n = order.length
  const reordered = Array(n).fill(null).map(() => Array(n).fill(0))

  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      reordered[i][j] = matrix[order[i]][order[j]]
    }
  }

  return reordered
}

/**
 * Reorder labels according to a given index ordering
 */
export function reorderLabels(labels, order) {
  return order.map(i => labels[i])
}

/**
 * Extract IQUP sub-dimension scores from calculated polos
 * Returns flat array of objects suitable for correlation analysis
 */
export function extractSubDimensionScores(calculatedPolos) {
  return calculatedPolos.map(polo => {
    const { Conectividade, Diversidade, Conforto } = polo.indicadores.Componentes

    return {
      id: polo.id,
      nome: polo.nome,
      nivel: polo.nivel,
      // Connectivity sub-dimensions
      pontoNodal: Conectividade.Componentes['Ponto Nodal'],
      acessos: Conectividade.Componentes.Acessos.Valor,
      // Diversity sub-dimensions
      usos: Diversidade.Componentes.Usos.Valor,
      arquitetura: Diversidade.Componentes.Arquitetura.Valor,
      // Comfort sub-dimensions
      sensorial: Conforto.Componentes.Sensorial.Valor,
      passeio: Conforto.Componentes.Passeio.Valor,
      // Main dimension totals (for reference)
      conectividade: Conectividade.Valor,
      diversidade: Diversidade.Valor,
      conforto: Conforto.Valor,
      iqup: polo.indicadores.Valor
    }
  })
}

/**
 * Sub-dimension metadata for display
 */
export const SUB_DIMENSION_META = {
  pontoNodal: {
    key: 'pontoNodal',
    label: 'Ponto Nodal',
    fullLabel: 'Ponto Nodal',
    parent: 'connectivity',
    description: 'Equipamentos urbanos e terminais de transporte'
  },
  acessos: {
    key: 'acessos',
    label: 'Acessos',
    fullLabel: 'Acessos',
    parent: 'connectivity',
    description: 'Ônibus, estacionamento e infraestrutura cicloviária'
  },
  usos: {
    key: 'usos',
    label: 'Usos',
    fullLabel: 'Usos',
    parent: 'diversity',
    description: 'Residencial/serviços, térreo, horário comercial e fim de semana'
  },
  arquitetura: {
    key: 'arquitetura',
    label: 'Arquitetura',
    fullLabel: 'Arquitetura',
    parent: 'diversity',
    description: 'Gabarito, testada e idade dos edifícios'
  },
  sensorial: {
    key: 'sensorial',
    label: 'Sensorial',
    fullLabel: 'Sensorial',
    parent: 'comfort',
    description: 'Arborização, proteção, limpeza, muro, fachada e travessia'
  },
  passeio: {
    key: 'passeio',
    label: 'Passeio',
    fullLabel: 'Passeio',
    parent: 'comfort',
    description: 'Largura, faixa livre, piso, conservação, degraus e entrada'
  }
}

export const SUB_DIMENSION_KEYS = Object.keys(SUB_DIMENSION_META)

export const PARENT_COLORS = {
  connectivity: '#dc3545',  // Bootstrap danger (red)
  diversity: '#0dcaf0',     // Bootstrap info (cyan)
  comfort: '#198754'        // Bootstrap success (green)
}
